import { Navigate, Link } from "react-router-dom";
import { Clock, LogOut, ShieldCheck, CreditCard, ClipboardList, Users } from "lucide-react";
import { useAuth, isStaff } from "@/context/AuthContext";
import { useFetch, fmtDate, usePageTitle } from "@/lib/api";
import { divisionName, SITE } from "@/lib/content";
import { Loading, EmptyState } from "@/components/site/Bits";
import { ProfileRequestCard } from "@/components/site/ProfileRequest";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="card-navy p-5" data-testid={testid}>
    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold"><Icon className="h-3.5 w-3.5" />{label}</p>
    <p className="mt-2 font-display text-2xl font-extrabold uppercase italic">{value || "Not yet recorded"}</p>
  </div>
);

const Approved = ({ user }) => {
  const { data, loading } = useFetch("/portal");
  if (loading) return <Loading />;
  const updates = data?.updates || [];
  return (
    <div className="space-y-10">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label={user.role === "player" ? "Player" : "Athlete(s)"} value={user.role === "player" ? user.name : user.athletes} testid="portal-athletes" />
        <Stat icon={ShieldCheck} label="Team" value={divisionName(user.team_slug)} testid="portal-team" />
        <Stat icon={ClipboardList} label="Registration Status" value={user.registration_status} testid="portal-registration-status" />
        <Stat icon={CreditCard} label="Payment Status" value={user.payment_status} testid="portal-payment-status" />
      </div>
      <p className="text-xs text-slate-500">Payment status is recorded by Tri County Allstars staff. Online payments are not processed through this website.</p>
      {user.role === "player" && <ProfileRequestCard />}
      <div>
        <p className="eyebrow mb-5">Private Team Updates</p>
        {updates.length ? (
          <div className="space-y-px bg-line/60" data-testid="portal-updates">
            {updates.map((u) => (
              <div key={u.id} className="bg-pitch py-5" data-testid={`portal-update-${u.id}`}>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{fmtDate(u.date)} • {u.team_slug === "all" ? "All Families" : divisionName(u.team_slug)}</p>
                <p className="font-display text-2xl font-extrabold uppercase italic">{u.title}</p>
                <p className="mt-1 text-sm text-slate-300 whitespace-pre-line">{u.body}</p>
              </div>
            ))}
          </div>
        ) : <EmptyState testid="portal-updates-empty" title="No private updates yet" body="Team updates from your coaches will appear here." />}
      </div>
    </div>
  );
};

export default function Portal() {
  usePageTitle("Parent Portal");
  const { user, logout } = useAuth();
  if (user === null) return <div className="wrap pt-40"><Loading /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <section className="bg-pitch min-h-screen pt-36 pb-20" data-testid="portal-page">
      <div className="wrap">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 border-b border-line pb-8 mb-10">
          <div>
            <p className="eyebrow" data-testid="portal-role-label">{{ parent: "Parent Portal", player: "Player Portal", coach: "Coach Account", admin: "Admin Account" }[user.role] || "Portal"}</p>
            <h1 className="h-display text-4xl sm:text-6xl mt-2">Welcome, {user.name}</h1>
          </div>
          <div className="flex gap-3">
            {isStaff(user) && <Link to="/admin" className="btn-gold !py-2.5" data-testid="portal-dashboard-link">Dashboard</Link>}
            <button onClick={logout} className="btn-line !py-2" data-testid="portal-logout-button"><LogOut className="h-4 w-4" /> Sign Out</button>
          </div>
        </div>
        {user.approved ? <Approved user={user} /> : (
          <div className="card-navy p-8 max-w-2xl" data-testid="portal-pending">
            <Clock className="h-10 w-10 text-gold" />
            <p className="mt-4 font-display text-3xl font-black uppercase italic">{user.role} account pending approval</p>
            <p className="mt-2 text-slate-300">Tri County Allstars staff review every account before private team information or dashboard tools are available. For questions, call {SITE.phone} or email {SITE.email}.</p>
          </div>
        )}
      </div>
    </section>
  );
}
